import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { BodyDesc, Frame, LoadResult, RunBundle } from "@picnic-field/local-host/protocol";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import { readLod } from "@picnic-field/renderer/lod";
import { useI18n } from "../../../i18n";
import { courseDeps } from "../lib/deps";
import type { CourseRun } from "../hooks/useCourseRun";
import { applyViewPrinciple, PrincipleLabelLayer, usePrincipleLabels } from "./Principle";
import type { PrincipleState, SceneMachine, ViewState, CourseViewLike } from "../types";
import { ZoomControls } from "../../../components/ZoomControls";
import { useOrbitDrag } from "../../../components/useOrbitDrag";

interface Tag {
  index: number;
  label: string;
  kind: "me" | "ghost" | "friend";
}

/** 프레임 공급자 — 코스 한 판(useCourseRun) · 친구 놀이 · 리플레이 재생이 같은 모양 */
export type SceneRun = Pick<CourseRun, "latestFrame" | "onFrame" | "probe">;

/**
 * FS-04-02 3D 코스 뷰(추적 카메라) + 이름표('내 기계' · 고스트 별명 — 시간 없음) + HUD·리모컨 자리(children).
 * 3D 가 안 되면(WebGL2 · GLB) 옆에서 본 그림(와이어 장면과 같은 모양)으로 바꾼다 — 코스는 계속 달릴 수 있다.
 * data-view: loading | 3d | schematic — E2E 가 첫 프레임을 기다리는 데 쓴다.
 */
export function CourseScene({
  run,
  load,
  bundle,
  palette,
  ghosts,
  friends = [],
  children,
  onView,
  label,
  meLabel,
  follow,
  principle,
  stageScene,
  stageThemeData,
  stageModelUrl,
  courseScene,
  orbit = false,
}: {
  run: SceneRun;
  load: LoadResult | null;
  bundle: RunBundle | null;
  palette: PaletteLike | null;
  ghosts: readonly SceneMachine[];
  friends?: readonly SceneMachine[];
  children?: ReactNode;
  onView?: (v: CourseViewLike | null) => void;
  /** 장면 이름(접근성) — 기본 '코스 장면' */
  label?: string;
  /** 내 기계 이름표 — 기본 '내 기계'(리플레이 뷰어는 기록한 학생 별명) */
  meLabel?: string;
  /** 추적 카메라(기본: 코스 — 친구 놀이는 더 뒤·위에서) */
  follow?: { back?: number; up?: number; side?: number; lookAhead?: number };
  /** P2-S5 원리 보기(SR-PHY-07) — 켜면 뷰 오버레이 + SU 수치 라벨(호스트 쪽 setPrinciple 은 화면이 건다) */
  principle?: PrincipleState;
  /** 기구 무대(2026-10-06) — 장면 키(하늘 · 바닥 · 소품 · 손님). 주면 배경색 대신 장면을 그린다 */
  stageScene?: string;
  /** 운영 콘솔이 배포한 무대 꾸밈(카탈로그 stage_scene payload · 2026-10-08) — 없으면 기본 장면 */
  stageThemeData?: unknown;
  /** 꾸밈의 올린 3D 모델 키 → GLB blob URL(2026-10-08) */
  stageModelUrl?: (model: string) => Promise<string | null>;
  /** 코스 도전 장면(2026-10-07) — course-park · course-line · course-walk(코스 둘레 소품 · 응원 손님) */
  courseScene?: string;
  /** 끌어서 둘러보기(2026-10-07 — 기구 무대 · 10-09 코스 도전도) · 두 번 누르면 처음 자리. 따라가는 카메라에 각을 더하므로 달리는 중에도 내 기계 둘레를 돈다 */
  orbit?: boolean;
}) {
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tagRefs = useRef(new Map<number, HTMLSpanElement>());
  const [view, setView] = useState<ViewState>("loading");
  const onViewRef = useRef(onView);
  onViewRef.current = onView;
  const viewRef = useRef<CourseViewLike | null>(null);
  const principleRef = useRef(principle);
  principleRef.current = principle;
  const labels = usePrincipleLabels();
  useOrbitDrag(canvasRef, () => viewRef.current, orbit);

  const tags = useMemo<Tag[]>(() => {
    if (!load) return [];
    const out: Tag[] = [];
    if (load.bodies[load.followIndex]?.owner === "me")
      out.push({ index: load.followIndex, label: meLabel ?? t("course.hud.me"), kind: "me" });
    const add = (xs: readonly SceneMachine[], kind: Tag["kind"]) => {
      for (const g of xs) {
        const b = load.bodies.find((x) => x.owner === g.id && x.kind !== "env");
        if (b) out.push({ index: b.index, label: g.label, kind });
      }
    };
    add(ghosts, "ghost");
    add(friends, "friend");
    return out;
  }, [load, ghosts, friends, t, meLabel]);

  // 3D 뷰 — 새 load(몸체 목록)마다 다시 만든다
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!load || !canvas || !bundle) return;
    let alive = true;
    let handle: CourseViewLike | null = null;
    const offs: (() => void)[] = [];
    setView("loading");
    courseDeps()
      .createView(canvas, {
        bodies: load.bodies,
        followIndex: load.followIndex,
        me: {
          glbUrl: bundle.asset.glbUrl,
          appearance: bundle.appearance,
          palette,
        },
        ghosts: ghosts.map((g) => ({
          ghostId: g.id,
          glbUrl: g.bundle.asset.glbUrl,
          appearance: g.bundle.appearance,
          palette,
        })),
        friends: friends.map((g) => ({
          ghostId: g.id,
          glbUrl: g.bundle.asset.glbUrl,
          // S5 LOD(run-bundle asset 의 선택 필드) — 친구는 L1
          lod: readLod(g.bundle.asset),
          appearance: g.bundle.appearance,
          palette,
        })),
        quality: lowEnd() ? "low" : "high",
        background: cssVar("--pf-color-scene", "#CFE3D5"),
        ...(stageScene ? { stageScene } : {}),
        ...(stageScene && stageThemeData != null ? { stageThemeData } : {}),
        ...(stageScene && stageModelUrl ? { stageModelUrl } : {}),
        ...(courseScene ? { courseScene } : {}),
        ...(follow ? { follow } : {}),
      })
      .then(async (h) => {
        handle = h;
        if (!alive) return h.dispose();
        await h.ready;
        if (!alive) return;
        const f = run.latestFrame();
        if (f) h.update(f);
        offs.push(run.onFrame((fr) => h.update(fr)));
        offs.push(
          h.onPaint((ts) => {
            run.probe.markPainted(ts);
            placeTags(h, tagRefs.current);
            if (principleRef.current?.on) labels.place(h);
          }),
        );
        viewRef.current = h;
        applyViewPrinciple(h, principleRef.current);
        setView("3d");
        onViewRef.current?.(h);
      })
      .catch(() => {
        if (alive) setView("schematic");
      });
    return () => {
      alive = false;
      offs.forEach((o) => o());
      handle?.dispose();
      viewRef.current = null;
      onViewRef.current?.(null);
    };
    // ghosts·palette 는 load 와 함께 바뀐다(준비 단계에서 고른 뒤 load)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, bundle]);

  // 원리 보기 토글 · 밀도(SU 수치)
  const pOn = principle?.on ?? false;
  const pNumbers = principle?.numbers ?? false;
  useEffect(() => {
    applyViewPrinciple(viewRef.current, { on: pOn, numbers: pNumbers });
    if (!pOn) labels.place(null);
    // labels 는 ref 묶음
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pOn, pNumbers, view]);

  return (
    <div
      className="pf-course__scene"
      data-view={view}
      data-principle={pOn ? (pNumbers ? "su" : "se") : "off"}
      data-testid="course-scene"
      role="img"
      aria-label={label ?? t("course.scene.label")}
    >
      <canvas ref={canvasRef} className="pf-course__canvas" hidden={view === "schematic"} />
      {view !== "schematic" && (
        <ZoomControls getView={() => viewRef.current} wheelTarget={canvasRef} />
      )}
      {view === "schematic" && load && <Schematic run={run} load={load} tagRefs={tagRefs} />}
      <PrincipleLabelLayer layer={labels.layer} on={pOn && pNumbers && view === "3d"} />
      {tags.map((g) => (
        <span
          key={g.index}
          ref={(el) => {
            if (el) tagRefs.current.set(g.index, el);
            else tagRefs.current.delete(g.index);
          }}
          className={`pf-course__tag pf-course__tag--${g.kind}`}
          data-testid={`tag-${g.kind}`}
          aria-hidden="true"
        >
          {g.label}
        </span>
      ))}
      {children}
    </div>
  );
}

function placeTags(h: CourseViewLike, refs: Map<number, HTMLSpanElement>) {
  for (const [index, el] of refs) {
    const p = h.project?.(index);
    if (!p) {
      el.style.visibility = "hidden";
      continue;
    }
    el.style.visibility = "visible";
    el.style.transform = `translate(${Math.round(p.x)}px, ${Math.round(p.y)}px) translate(-50%, -100%)`;
  }
}

function lowEnd(): boolean {
  // ARC §7.5 — 저사양(코어 ≤4 또는 메모리 ≤4GB)은 해상도 스케일 0.75
  const n = navigator as Navigator & { deviceMemory?: number };
  return (n.hardwareConcurrency ?? 8) <= 4 || (n.deviceMemory ?? 8) <= 4;
}

function cssVar(name: string, fallback: string): string {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (/^#[0-9a-f]{3,8}$/i.test(v)) return v;
  } catch {
    /* 없음 */
  }
  return fallback;
}

// ── 옆에서 본 그림(3D 대체) ───────────────────────────────────────────────

const pose = (tf: Float32Array, i: number) => ({
  x: tf[i * 7] ?? 0,
  y: tf[i * 7 + 1] ?? 0,
  z: tf[i * 7 + 2] ?? 0,
});

function Schematic({
  run,
  load,
  tagRefs,
}: {
  run: SceneRun;
  load: LoadResult;
  tagRefs: React.MutableRefObject<Map<number, HTMLSpanElement>>;
}) {
  const svg = useRef<SVGSVGElement>(null);
  const [frame, setFrame] = useState<Frame | null>(() => run.latestFrame());

  useEffect(() => {
    let raf = 0;
    let pending: Frame | null = null;
    const off = run.onFrame((f) => {
      pending = f;
      if (!raf)
        raf = requestAnimationFrame((ts) => {
          raf = 0;
          setFrame(pending);
          run.probe.markPainted(ts);
        });
    });
    return () => {
      off();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [run]);

  const env = load.bodies.filter((b) => (b.kind === "env" || b.kind === "zone") && b.shape);
  const tf = frame?.transforms;
  // 코스 범위(z) → 가로, 높이(y) → 세로
  const extent = useMemo(() => {
    if (!tf) return { z0: 0, z1: 1, top: 0.3 };
    let z0 = Infinity;
    let z1 = -Infinity;
    let top = 0.1;
    for (const b of env) {
      const p = pose(tf, b.index);
      const hz = halfZ(b);
      z0 = Math.min(z0, p.z - hz);
      z1 = Math.max(z1, p.z + hz);
      top = Math.max(top, p.y + halfY(b));
    }
    return { z0, z1, top };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, !!tf]);

  useEffect(() => {
    if (!tf || !svg.current) return;
    const r = svg.current.getBoundingClientRect();
    for (const [index, el] of tagRefs.current) {
      const p = pose(tf, index);
      const x = ((p.z - extent.z0) / (extent.z1 - extent.z0)) * (r.width || 600);
      const y =
        baseY(r.height || 300) - (p.y / Math.max(0.2, extent.top)) * (r.height || 300) * 0.3;
      el.style.visibility = "visible";
      el.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px) translate(-50%, -100%)`;
    }
  });

  return (
    <svg
      ref={svg}
      className="pf-course__schematic"
      viewBox="0 0 1000 400"
      preserveAspectRatio="none"
      aria-hidden="true"
      data-testid="course-schematic"
    >
      {tf &&
        env.map((b) => {
          const p = pose(tf, b.index);
          const x0 = ((p.z - halfZ(b) - extent.z0) / (extent.z1 - extent.z0)) * 1000;
          const x1 = ((p.z + halfZ(b) - extent.z0) / (extent.z1 - extent.z0)) * 1000;
          const h = (y: number) => 400 * 0.5 - (y / Math.max(0.2, extent.top)) * 400 * 0.3;
          if (b.shape?.type === "ramp") {
            // 쐐기: −z 낮은 끝(밑면) → +z 높은 끝(윗면)
            const hy = b.shape.half[1];
            return (
              <polygon
                key={b.index}
                className="pf-course__schem-ramp"
                points={`${x0},${h(p.y - hy)} ${x1},${h(p.y + hy)} ${x1},${h(p.y - hy)}`}
              />
            );
          }
          const y0 = h(p.y + halfY(b));
          return (
            <rect
              key={b.index}
              className={`pf-course__schem-${b.kind === "zone" ? b.surface : "ground"}`}
              x={x0}
              y={y0}
              width={Math.max(1, x1 - x0)}
              height={b.kind === "zone" ? 6 : Math.max(5, h(0) - y0 + 5)}
            />
          );
        })}
    </svg>
  );
}

const baseY = (h: number) => h * 0.5;

function halfZ(b: BodyDesc): number {
  if (!b.shape) return 0;
  if (b.shape.type === "cylinder") return b.shape.radius;
  if (b.shape.type === "ramp")
    return b.shape.half[2] * Math.cos((b.shape.angleDeg * Math.PI) / 180);
  return b.shape.half[2];
}

function halfY(b: BodyDesc): number {
  if (!b.shape) return 0;
  if (b.shape.type === "cylinder") return b.shape.halfHeight;
  return b.shape.half[1];
}
